import type { PhoneNumber, CountryCode } from 'libphonenumber-js'
import { parsePhoneNumber } from 'libphonenumber-js'

/**
 * Class representing a normalized phone number.
 * Once normalized you can get the local, international or E.164 value.
 */
export class NormalizedPhoneNumber {
  readonly error: boolean

  readonly originalNumber: string

  readonly number: PhoneNumber

  /**
   * Create a normalized phone number.
   * @param {string} phoneNumber Phone number either in E.164, local or international form.
   * @param {string} country Country code for the number. This is needed if the phone number is in the local form.
   */
  constructor(phoneNumber: string, country?: string) {
    this.originalNumber = phoneNumber

    try {
      this.number = parsePhoneNumber(phoneNumber, country as CountryCode)
      this.error = false
    } catch {
      this.error = true
    }
  }

  /**
   * Get the flag that indicates if the number is valid.
   * @return {boolean} The true if the number is valid.
   */
  get valid(): boolean {
    return !this.error && this.number.isValid()
  }

  /**
   * Get the phone number in the E.164 format such as +15103836075.
   * @return {string} The number in the E.164 format.
   */
  get iso(): string {
    return this.error ? this.originalNumber : this.number.number
  }

  /**
   * Get the phone number in the international format such as +1 510-383-6075.
   * @return {string} The number in the international format.
   */
  get international(): string {
    return this.error ? this.originalNumber : this.number.formatInternational()
  }

  /**
   * Get the phone number in the local format such as (510) 383-6075 (USA) or 0211 1234567 (Germany).
   * @return {string} The number in the local format.
   */
  get local(): string {
    return this.error ? this.originalNumber : this.number.formatNational()
  }

  /**
   * Get the ISO 3166 country code of the phone number such as US or DE.
   * @return {string} The SO 3166 country code.
   */
  get countryIso(): string {
    return this.error ? '' : this.number.country ?? ''
  }

  /**
   * Get the country code part of the phone number such as 1 (US or Canada) or 49 (Germany).
   * @return {number} The SO 3166 country code.
   */
  get countryCode(): number {
    return this.error ? 0 : parseInt(this.number.countryCallingCode, 10)
  }
}
